/**
 * 無限スクロールタイムラインビュー
 *
 * Slackライクな無限スクロールでアクティビティを表示
 * react-virtuosoを使用して仮想スクロール対応
 */

import { Book, Dumbbell, Loader2, MapPin, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { GroupedVirtuoso, type GroupedVirtuosoHandle } from 'react-virtuoso'
import { toast } from 'sonner'

import type { ReadHistory } from '@/api/type'
import type { CardTransaction } from '@/api/types/card-transaction'
import type { GarminActivitySummary, GarminSleepRaw, GarminSteps15Min } from '@/api/types/garmin'
import type { OverlandLocation, OverlandVisit } from '@/api/types/overland'
import type { ReceiptSummary } from '@/api/types/receipt'
import { Button } from '@/components/ui/button'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { isSameDate } from '@/lib/date-utils'
import { formatDateCompact, formatRelativeDate } from '@/lib/format-utils'
import { listSleepEpisodes } from '@/lib/garmin-sleep'
import type { MovementSessionWithVisits } from '@/lib/movement-session'
import {
  buildVisitCardTransactionMap,
  buildVisitReceiptMap,
  listUnassignedSpendings,
  type UnassignedSpending,
} from '@/lib/receipt-visit-utils'
import { useActivityReviewStore } from '@/store/useActivityReviewStore'
import { useAppStore } from '@/store/useAppStore'
import { useProgressStore } from '@/store/useProgressStore'

import { INFINITE_TIMELINE_TYPES, type TimelineItemType, useTimelineItemFilter } from '../timeline-item-filters'
import { useActivityDayPaging } from '../use-day-paging'
import { GarminActivityCard } from './activity-card/garmin-activity-card'
import { OverlandMovementCard } from './activity-card/overland-movement-card'
import { OverlandVisitCard } from './activity-card/overland-visit-card'
import { ReadCard } from './activity-card/read-card'
import { SleepCard } from './activity-card/sleep-card'
import { SpendingRow } from './activity-card/spending-row'
import { ReviewProgressBar } from './review-progress-bar'
import { SpendingSummary } from './spending-summary'
import { StepsSummary } from './steps-timeline'
import { TimelineItemFilterChips } from './timeline-item-filter-chips'

// タイムラインアイテム
type TimelineItem =
  | { type: 'read'; data: ReadHistory; timestamp: Date }
  | { type: 'garmin'; data: GarminActivitySummary; timestamp: Date }
  | {
      type: 'visit'
      data: OverlandVisit
      locations: OverlandLocation[]
      receipts: ReceiptSummary[] | undefined
      cardTransactions: CardTransaction[] | undefined
      timestamp: Date
    }
  | { type: 'movement'; data: MovementSessionWithVisits; timestamp: Date }
  | { type: 'sleep'; data: GarminSleepRaw; timestamp: Date }
  | { type: 'steps-summary'; data: GarminSteps15Min[]; timestamp: Date }
  | { type: 'spending'; data: UnassignedSpending; timestamp: Date }

const toFilterType = (type: TimelineItem['type']): TimelineItemType => (type === 'steps-summary' ? 'steps' : type)

type Props = {
  /** ヘッダで選んだ日付(YYYY-MM-DD)。変わるとその日までスクロールする */
  targetDate?: string
}

export const InfiniteTimelineView = ({ targetDate }: Props) => {
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const { removeReadHistory } = useProgressStore()
  const reviewedThroughDate = useActivityReviewStore((state) => state.reviewedThroughDate)
  const setReviewedThrough = useActivityReviewStore((state) => state.setReviewedThrough)
  const { isSubmitting: isMarkingReviewed, run: runMarkReviewed } = useSubmitLock()

  // その日までをまとめて確認済みにする
  const handleMarkReviewed = useCallback(
    async (dateStr: string) => {
      await runMarkReviewed(async () => {
        const succeeded = await setReviewedThrough(dateStr)
        if (succeeded) {
          toast.success(`${formatDateCompact(dateStr)} まで確認済みにしました`)
        } else {
          toast.error('確認済みの更新に失敗しました')
        }
      })
    },
    [runMarkReviewed, setReviewedThrough],
  )

  // タイムラインに出す項目の絞り込み（初期値は全表示）
  const { hiddenTypes, toggleType } = useTimelineItemFilter()

  const { dayDataMap, loadedDates, isLoadingMore, isJumping, loadMorePast, jump, pendingJumpDate, clearPendingJump } =
    useActivityDayPaging(targetDate)

  // 指定日へのジャンプ（ロード完了後にスクロールする）
  const virtuosoRef = useRef<GroupedVirtuosoHandle>(null)

  // 日付ごとのタイムラインアイテムを生成
  const { groupCounts, flatItems, groupDates } = useMemo(() => {
    const groups: { date: string; items: TimelineItem[] }[] = []

    for (const dateStr of loadedDates) {
      const dayData = dayDataMap.get(dateStr)
      if (!dayData) continue

      const items: TimelineItem[] = []
      const targetDate = new Date(dateStr)

      // 読書履歴
      dayData.readHistory.forEach((item) => {
        items.push({
          type: 'read',
          data: item,
          timestamp: new Date(item.started_at),
        })
      })

      // Garminアクティビティ
      dayData.activities.forEach((activity) => {
        const activityDate = new Date(activity.start_time)
        if (isSameDate(activityDate, targetDate)) {
          items.push({
            type: 'garmin',
            data: activity,
            timestamp: activityDate,
          })
        }
      })

      // Overland訪問（買い物はこの日に出す滞在にだけ割り当て、残りは単独の行にする）
      const shownVisits = dayData.visits.filter((visit) =>
        isSameDate(new Date(visit.arrival_date ?? visit.timestamp), targetDate),
      )
      const visitReceiptMap = buildVisitReceiptMap(shownVisits, dayData.receipts)
      const visitCardTxMap = buildVisitCardTransactionMap(shownVisits, dayData.cardTransactions)
      shownVisits.forEach((visit) => {
        items.push({
          type: 'visit',
          data: visit,
          locations: dayData.locations,
          receipts: visitReceiptMap.get(visit.id),
          cardTransactions: visitCardTxMap.get(visit.id),
          timestamp: new Date(visit.arrival_date ?? visit.timestamp),
        })
      })

      // 移動セッション
      dayData.movementSessions.forEach((session) => {
        items.push({
          type: 'movement',
          data: session,
          timestamp: new Date(session.startTime),
        })
      })

      // 睡眠（その日に起きた睡眠すべて）
      listSleepEpisodes(dayData.sleep).forEach((episode) => {
        const end = episode.dailySleepDTO?.sleepEndTimestampGMT
        if (!end) return
        const sleepEndDate = new Date(end)
        if (isSameDate(sleepEndDate, targetDate)) {
          items.push({ type: 'sleep', data: episode, timestamp: sleepEndDate })
        }
      })

      // 歩数サマリー（その日の最初のアイテムとして追加）
      const dayStart = new Date(`${dateStr}T00:00:00`)
      if (dayData.steps15Min.length > 0) {
        items.push({
          type: 'steps-summary',
          data: dayData.steps15Min,
          timestamp: dayStart,
        })
      }

      // 滞在に入らなかった買い物も、合計金額だけでなく行として出す
      listUnassignedSpendings(
        dayData.receipts,
        visitReceiptMap,
        dayData.cardTransactions,
        visitCardTxMap,
        dayStart,
      ).forEach((spending) => {
        items.push({ type: 'spending', data: spending, timestamp: spending.timestamp })
      })

      // 時間順にソート（古い順）
      items.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())

      const visibleItems = items.filter((item) => !hiddenTypes.has(toFilterType(item.type)))

      if (visibleItems.length > 0) {
        groups.push({ date: dateStr, items: visibleItems })
      }
    }

    // 日付の順序を古い順に並べ替え（一番下が今日になるように）
    groups.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

    const flatItems: TimelineItem[] = []
    const groupCounts: number[] = []
    const groupDates: string[] = []

    groups.forEach((group) => {
      groupCounts.push(group.items.length)
      groupDates.push(group.date)
      flatItems.push(...group.items)
    })

    return { groupCounts, flatItems, groupDates }
  }, [loadedDates, dayDataMap, hiddenTypes])

  // 各グループの先頭アイテムが flatItems の何番目か
  const groupStartIndices = useMemo(() => {
    const indices: number[] = []
    let acc = 0
    for (const count of groupCounts) {
      indices.push(acc)
      acc += count
    }
    return indices
  }, [groupCounts])

  // ロード完了後に対象日へスクロール（その日にアイテムがなければ直後の日へ）
  useEffect(() => {
    if (!pendingJumpDate) return

    // 取得が終わるまでグループが現れないので、見つかるまで持ち越す
    const groupIndex = groupDates.findIndex((d) => d >= pendingJumpDate)
    if (groupIndex < 0) return
    clearPendingJump()

    const itemIndex = groupStartIndices.at(groupIndex) ?? 0
    // Virtuoso の再レンダリング後に呼ばないと位置がずれる
    const frameId = requestAnimationFrame(() => {
      virtuosoRef.current?.scrollToIndex({ index: itemIndex, align: 'start' })
    })
    return () => cancelAnimationFrame(frameId)
  }, [pendingJumpDate, groupDates, groupStartIndices, clearPendingJump])

  const isEmpty = loadedDates.length > 0 && flatItems.length === 0 && !isLoadingMore

  return (
    <div className="flex h-full flex-col">
      <ReviewProgressBar onJump={jump} isJumping={isJumping} />
      {hasFullAccess && (
        <TimelineItemFilterChips types={INFINITE_TIMELINE_TYPES} hiddenTypes={hiddenTypes} onToggle={toggleType} />
      )}

      {isEmpty ? (
        <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-4 p-8">
          <div className="flex items-center gap-2">
            <Book className="size-12 opacity-50" />
            {hasFullAccess && <Dumbbell className="size-12 opacity-50" />}
            {hasFullAccess && <MapPin className="size-12 opacity-50" />}
          </div>
          <p>アクティビティはありません</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          <GroupedVirtuoso
            ref={virtuosoRef}
            groupCounts={groupCounts}
            groupContent={(index) => {
              const groupDate = groupDates.at(index) ?? ''
              const isReviewed = !!reviewedThroughDate && groupDate <= reviewedThroughDate
              return (
                <div className="sticky top-0 z-10 flex items-center gap-2 border-b bg-background/95 px-4 py-2 backdrop-blur-sm">
                  <span className="text-sm font-medium">{formatRelativeDate(groupDate)}</span>
                  {hasFullAccess && !isReviewed && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 shrink-0 px-2 text-xs"
                      onClick={() => handleMarkReviewed(groupDate)}
                      disabled={isMarkingReviewed}
                    >
                      ここまで確認
                    </Button>
                  )}
                  <div className="ml-auto">
                    <SpendingSummary
                      receipts={dayDataMap.get(groupDate)?.receipts ?? []}
                      cardTransactions={dayDataMap.get(groupDate)?.cardTransactions}
                    />
                  </div>
                </div>
              )
            }}
            itemContent={(index) => {
              const item = flatItems.at(index)
              if (!item) return null

              return (
                <div className="px-4 py-1">
                  {item.type === 'read' && (
                    <div className="relative group">
                      <ReadCard item={item.data} />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                        onClick={() => removeReadHistory(item.data.id)}
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </div>
                  )}

                  {item.type === 'garmin' && <GarminActivityCard activity={item.data} />}

                  {item.type === 'visit' && (
                    <OverlandVisitCard
                      visit={item.data}
                      locations={item.locations}
                      receipts={item.receipts}
                      cardTransactions={item.cardTransactions}
                    />
                  )}

                  {item.type === 'movement' && <OverlandMovementCard session={item.data} />}

                  {item.type === 'sleep' && <SleepCard sleep={item.data} />}

                  {item.type === 'steps-summary' && (
                    <div className="flex justify-end">
                      <StepsSummary steps15Min={item.data} />
                    </div>
                  )}

                  {item.type === 'spending' && <SpendingRow spending={item.data} />}
                </div>
              )
            }}
            startReached={loadMorePast}
            initialTopMostItemIndex={flatItems.length > 0 ? flatItems.length - 1 : 0}
            followOutput="auto"
            alignToBottom
            components={{
              Header: () =>
                isLoadingMore ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="size-6 animate-spin text-muted-foreground" />
                  </div>
                ) : null,
            }}
          />
        </div>
      )}
    </div>
  )
}
